import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Descriptions, Tag, Button, Popconfirm, Modal, Input, Timeline, Empty, Tooltip } from 'antd';
import { toast } from 'react-toastify';
import Icons from '@/components/third-party/Icons';
import PageModal from '@/components/PageModal';
import ESignAuth from '@/components/ESignAuth';
import {
  get_production_demand_detail,
  submit_production_demand,
  approve_production_demand,
  approve_demand_workflow_step,
  reject_demand_workflow_step,
  get_demand_approval_history,
} from '@/utils/api';
import {
  fmtDate,
  priorityTagColor,
  demandStatusTagColor,
  workflowStepStatusColor,
  canSubmitForApproval,
  personName,
} from './helpers';
import CreateLotsModal from './CreateLotsModal';
import EditDemandModal from './EditDemandModal';

// ==============================|| PRODUCTION DEMAND DETAIL DRAWER ||============================== //

const DemandDetailDrawer = ({ open, onClose, demandId, accessData, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [lotsOpen, setLotsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [esignOpen, setEsignOpen] = useState(false);

  const fetchDetail = () => {
    if (!demandId) return;
    setLoading(true);
    get_production_demand_detail(demandId, (data) => {
      setLoading(false);
      if (data?.success) setDetail(data.data);
    });
    // No workflow yet on a given demand simply means an empty history — not an error.
    get_demand_approval_history(demandId, (data) => {
      if (data?.success) setHistory(data.data || []);
    });
  };

  useEffect(() => {
    if (open) fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, demandId]);

  const handleSubmit = () => {
    setActionLoading(true);
    submit_production_demand(
      demandId,
      (data) => {
        setActionLoading(false);
        if (data?.status) {
          // Echo the real returned status — once backend ships the workflow engine
          // this will read "Pending Production Manager Approval" etc. on its own,
          // no further frontend change needed.
          toast.success(`Demand submitted — ${data.status}`);
          fetchDetail();
          onChanged?.();
        } else {
          toast.error(data?.message || 'Failed to submit demand');
        }
      },
      { onError: () => setActionLoading(false) }
    );
  };

  // `pending_step_id` only exists once the workflow engine is live (backend not built
  // yet — see the "Backend needed" spec). Until then this falls back to the original
  // single-shot Draft -> Submitted -> Approved flow via approve_production_demand.
  const handleApprove = () => {
    setActionLoading(true);
    if (detail?.pending_step_id) {
      approve_demand_workflow_step(
        detail.pending_step_id,
        {},
        (data) => {
          setActionLoading(false);
          if (data?.success) {
            toast.success(
              data?.workflow_status === 'Approved'
                ? 'Demand fully approved'
                : 'Approved — sent to the next approver in the sequence'
            );
            fetchDetail();
            onChanged?.();
          } else {
            toast.error(data?.message || 'Failed to approve demand');
          }
        },
        { onError: () => setActionLoading(false) }
      );
      return;
    }
    approve_production_demand(
      demandId,
      (data) => {
        setActionLoading(false);
        if (data?.status) {
          toast.success('Demand approved');
          fetchDetail();
          onChanged?.();
        } else {
          toast.error(data?.message || 'Failed to approve demand');
        }
      },
      { onError: () => setActionLoading(false) }
    );
  };

  const handleReject = () => {
    let comment = '';
    Modal.warning({
      title: 'Rejection Comment',
      cancelText: 'Cancel',
      closable: true,
      maskClosable: true,
      content: (
        <Input.TextArea
          rows={3}
          placeholder="Enter reason for rejection (required)"
          onChange={(e) => {
            comment = e.target.value;
          }}
        />
      ),
      onOk() {
        if (!comment.trim()) {
          toast.error('Please enter a reason for rejection');
          return Promise.reject();
        }
        reject_demand_workflow_step(detail.pending_step_id, { comment: comment.trim() }, (data) => {
          if (data?.success) {
            toast.success('Demand rejected');
            fetchDetail();
            onChanged?.();
          } else {
            toast.error(data?.message || 'Failed to reject demand');
          }
        });
      },
    });
  };

  // Same graceful-degradation rule used across this feature: prefer the workflow
  // engine's own `workflow_status` once backend returns it, else fall back to the
  // simple `status` field so nothing regresses before that ships.
  const lotsEnabled = detail
    ? (detail.workflow_status ? detail.workflow_status === 'Approved' : detail.status === 'Approved')
    : false;

  return (
    <PageModal
      icon={<Icons.Eye size={22} />}
      title={`Demand — ${detail?.batch_number || ''}`}
      width={720}
      open={open}
      onCancel={onClose}
      destroyOnHidden
    >
      {detail && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Tag color={demandStatusTagColor(detail.status)}>{detail.status}</Tag>
            <Tag color={priorityTagColor(detail.priority)}>{detail.priority}</Tag>
            {detail.pending_approver_role && (
              <Tag color="gold">Pending: {detail.pending_approver_role}</Tag>
            )}
          </div>

          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Product">{detail.product_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Recipe">{detail.recipe_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Plant">{detail.plant_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Required Quantity (Batch Size)">{detail.required_quantity}</Descriptions.Item>
            <Descriptions.Item label="Start Date">{fmtDate(detail.start_date)}</Descriptions.Item>
            <Descriptions.Item label="End Date">{fmtDate(detail.end_date)}</Descriptions.Item>
            <Descriptions.Item label="Created By">{personName(detail.created_by)}</Descriptions.Item>
            <Descriptions.Item label="Approved By">{personName(detail.approved_by)}</Descriptions.Item>
          </Descriptions>

          <div className="flex gap-2 flex-wrap">
            {accessData?.update_access && canSubmitForApproval(detail.status) && (
              <Button onClick={() => setEditOpen(true)}>Edit</Button>
            )}
            {accessData?.update_access && (
              <Button
                disabled={!canSubmitForApproval(detail.status)}
                loading={actionLoading}
                onClick={handleSubmit}
              >
                {String(detail.status).toLowerCase().startsWith('rejected') ? 'Resubmit for Approval' : 'Submit for Approval'}
              </Button>
            )}
            {accessData?.update_access &&
              (detail.pending_step_esign_required ? (
                <Button
                  type="primary"
                  disabled={!(detail.pending_step_id || detail.status === 'Submitted')}
                  loading={actionLoading}
                  onClick={() => setEsignOpen(true)}
                >
                  Approve (E-Sign Required)
                </Button>
              ) : (
                <Popconfirm
                  title="Approve this production demand?"
                  okText="Yes"
                  cancelText="No"
                  onConfirm={handleApprove}
                  disabled={!(detail.pending_step_id || detail.status === 'Submitted')}
                >
                  <Button
                    type="primary"
                    disabled={!(detail.pending_step_id || detail.status === 'Submitted')}
                    loading={actionLoading}
                  >
                    Approve
                  </Button>
                </Popconfirm>
              ))}
            {/* Reject only exists on the workflow-engine path — the original simple
                Draft/Submitted/Approved flow has no reject endpoint. */}
            {accessData?.update_access && detail.pending_step_id && (
              <Button danger onClick={handleReject}>
                Reject
              </Button>
            )}
            <Tooltip title={lotsEnabled ? '' : 'Lots can only be managed once the demand is fully approved'}>
              <Button disabled={!lotsEnabled} onClick={() => setLotsOpen(true)}>
                Manage Lots
              </Button>
            </Tooltip>
          </div>

          <div>
            <div className="font-medium mb-2">Approval History</div>
            {history.length === 0 ? (
              <Empty
                description={
                  detail.status === 'Draft'
                    ? 'Not yet submitted for approval'
                    : 'No step-by-step approval history available for this demand'
                }
              />
            ) : (
              <Timeline
                items={history.map((step) => ({
                  color: workflowStepStatusColor(step.status),
                  children: (
                    <div className="flex flex-col">
                      <span>
                        <b>Step {step.sequence}</b> — {step.role || step.department_name || '—'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {personName(step.approver)} · {step.status}
                        {step.acted_at ? ` · ${fmtDate(step.acted_at)}` : ''}
                      </span>
                      {step.comment && <span className="text-xs text-red-600">"{step.comment}"</span>}
                    </div>
                  ),
                }))}
              />
            )}
          </div>
        </div>
      )}

      <CreateLotsModal
        open={lotsOpen}
        onClose={() => setLotsOpen(false)}
        demand={detail}
        canCreate={accessData?.create_access}
        onLotsChanged={onChanged}
      />

      <EditDemandModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        demand={detail}
        onUpdated={() => {
          fetchDetail();
          onChanged?.();
        }}
      />

      <ESignAuth
        visible={esignOpen}
        onCancel={() => setEsignOpen(false)}
        onSubmit={() => {
          handleApprove();
          setEsignOpen(false);
        }}
        req={{
          batch_number: detail?.batch_number,
          action_type: 'Approve',
          workflow_step_id: detail?.pending_step_id,
        }}
      />
    </PageModal>
  );
};

DemandDetailDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  demandId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  accessData: PropTypes.object,
  onChanged: PropTypes.func,
};

export default DemandDetailDrawer;
